import { useEffect, useRef, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Plus,
  RotateCcw,
  Save,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { CustomDatePicker, CustomSearchInput, CustomSelect, FeedbackMessage } from '../common';
import { createPurchaseOrderFormInitialValues } from '../../data/purchaseOrderConstants';
import {
  deleteInitialSummaryPurchaseOrder,
  fetchInitialSummaryPurchaseOrder,
  saveInitialSummaryPurchaseOrder,
} from '../../services/purchaseOrderService';

const SACKS_PER_FCL = 280;
const SUPPLIER_BATCH_DEFAULT_WAREHOUSE_KEY = 'seaweed:supplier-batch-default-warehouse';

function PurchaseOrderSupplierBatch({
  supplierOptions,
  warehouseLocationOptions = [],
  purchaseOrders = [],
  supplierBalancings = [],
  isSaving,
  isPageLoading = false,
  canSubmit,
  submittedPoDates,
  draftPoDates,
  onSubmit,
  onDraftSaved,
  onRequestClearConfirmation,
  onRequestSubmitConfirmation,
  onRequestReopenConfirmation,
  onRequestRemoveGeneratedPurchaseOrderConfirmation,
  onRemoveGeneratedPurchaseOrder,
  onAddSupplier,
}) {
  const [values, setValues] = useState(() => createInitialBatchValues());
  const [errors, setErrors] = useState({});
  const [draftMessage, setDraftMessage] = useState('');
  const [isDraftLoading, setIsDraftLoading] = useState(true);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveBadgeToken, setAutoSaveBadgeToken] = useState(0);
  const hasStartedAutoSave = useRef(false);
  const skipNextAutoSave = useRef(false);
  const quantityInputRefs = useRef(new Map());
  const pendingQuantityFocusSupplierId = useRef('');
  const selectedSupplierIds = useMemo(
    () => new Set(values.items.map((item) => String(item.supplierId))),
    [values.items]
  );
  const totalSacks = useMemo(() => getTotalSacks(values.items), [values.items]);
  const hasPriceRange = hasPriceRangeValues(values);
  const selectedDateStatus = useMemo(() => {
    if (
      values.status === 'Submitted' ||
      (values.status !== 'Draft' && submittedPoDates?.has(values.poDate))
    ) {
      return 'submitted';
    }

    if (values.items.length || hasPriceRange || draftPoDates?.has(values.poDate)) {
      return 'draft';
    }

    return 'no-entry';
  }, [
    draftPoDates,
    hasPriceRange,
    submittedPoDates,
    values.items.length,
    values.poDate,
    values.status,
  ]);
  const isSubmittedSummary = selectedDateStatus === 'submitted';
  const protectedPurchaseOrderIds = useMemo(
    () => buildProtectedPurchaseOrderIdSet(supplierBalancings),
    [supplierBalancings]
  );
  const highlightedCalendarDates = useMemo(
    () => ({
      submitted: Array.from(submittedPoDates || []).map(parseDateInputValue).filter(Boolean),
      draft: Array.from(draftPoDates || []).map(parseDateInputValue).filter(Boolean),
    }),
    [draftPoDates, submittedPoDates]
  );
  const draftValues = useMemo(
    () => ({
      poDate: values.poDate,
      warehouseLocation: values.warehouseLocation,
      priceMin: values.priceMin,
      priceMax: values.priceMax,
      estimatedFclQuantity: values.estimatedFclQuantity,
      status: 'Draft',
      items: withBatchWarehouseLocation(values.items, values.warehouseLocation),
    }),
    [
      values.estimatedFclQuantity,
      values.items,
      values.poDate,
      values.priceMax,
      values.priceMin,
      values.warehouseLocation,
    ]
  );

  useEffect(() => {
    const supplierId = pendingQuantityFocusSupplierId.current;

    if (!supplierId) {
      return;
    }

    const quantityInput = quantityInputRefs.current.get(String(supplierId));

    if (!quantityInput) {
      return;
    }

    window.requestAnimationFrame(() => {
      quantityInput.focus();
      quantityInput.select();
    });
    pendingQuantityFocusSupplierId.current = '';
  }, [values.items]);
  const availableSuppliers = useMemo(
    () =>
      supplierOptions.filter(
        (supplier) => !selectedSupplierIds.has(String(supplier.value))
      ),
    [selectedSupplierIds, supplierOptions]
  );
  const filteredSuppliers = useMemo(() => {
    const query = values.search.trim().toLowerCase();

    if (!query) {
      return availableSuppliers;
    }

    return availableSuppliers.filter((supplier) =>
      getSupplierSearchText(supplier).toLowerCase().includes(query)
    );
  }, [availableSuppliers, values.search]);
  const supplierSuggestions = useMemo(
    () =>
      availableSuppliers.map((supplier) => ({
        group: 'supplier',
        value: supplier.label,
        label: supplier.label,
        detail: supplier.sacksMark ? `Sacks Mark: ${supplier.sacksMark}` : '',
        searchValue: getSupplierSearchText(supplier),
        supplierId: supplier.value,
        actionLabel: 'Click to Select',
      })),
    [availableSuppliers]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadDraft() {
      if (!values.poDate) {
        setIsDraftLoading(false);
        return;
      }

      setIsDraftLoading(true);

      try {
        const savedInitialSummary = await fetchInitialSummaryPurchaseOrder(values.poDate);

        if (isMounted) {
          skipNextAutoSave.current = true;
          const savedValues = savedInitialSummary
            ? normalizeSavedInitialSummary(savedInitialSummary)
            : {
                priceMin: '',
                priceMax: '',
                estimatedFclQuantity: '',
                status: 'Draft',
                items: [],
              };
          const defaultWarehouseLocation =
            savedValues.warehouseLocation || getStoredDefaultWarehouseLocation();

          setValues((currentValues) => ({
            ...currentValues,
            search: '',
            ...savedValues,
            warehouseLocation: defaultWarehouseLocation,
            items: savedValues.warehouseLocation
              ? savedValues.items
              : withBatchWarehouseLocation(savedValues.items, defaultWarehouseLocation),
          }));
          setErrors({});
        }
      } catch (error) {
        if (isMounted) {
          setDraftMessage(error.message);
        }
      } finally {
        if (isMounted) {
          setIsDraftLoading(false);
        }
      }
    }

    loadDraft();

    return () => {
      isMounted = false;
    };
  }, [values.poDate]);

  useEffect(() => {
    if (isDraftLoading) {
      return undefined;
    }

    if (!hasStartedAutoSave.current) {
      hasStartedAutoSave.current = true;
      skipNextAutoSave.current = false;
      return undefined;
    }

    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        if (draftValues.items.length || hasPriceRangeValues(draftValues)) {
          if (Object.keys(validatePriceRange(draftValues)).length) {
            return;
          }

          await saveInitialSummaryPurchaseOrder(draftValues);
        } else {
          await deleteInitialSummaryPurchaseOrder(draftValues.poDate);
        }
        setDraftMessage('');
        setAutoSaveBadgeToken(Date.now());
      } catch (error) {
        if (!isEmptyInitialSummaryMessage(error.message)) {
          setDraftMessage(error.message);
        }
      }
    }, 550);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [draftValues, isDraftLoading]);

  useEffect(() => {
    if (!autoSaveBadgeToken) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setAutoSaveBadgeToken(0);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoSaveBadgeToken]);

  function handleDateChange(event) {
    const { name, value } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      status: 'Draft',
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
    }));
    setDraftMessage('');
  }

  function handlePriceRangeChange(event) {
    const { name, value } = event.target;
    const sanitizedValue = sanitizeMoneyInput(value);

    setValues((currentValues) => ({
      ...currentValues,
      [name]: sanitizedValue,
      status: 'Draft',
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      priceMin: '',
      priceMax: '',
    }));
    setDraftMessage('');
  }

  function handleFclQuantityChange(event) {
    if (isSubmittedSummary) {
      return;
    }

    const sanitizedValue = String(event.target.value || '').replace(/\D/g, '');

    setValues((currentValues) => ({
      ...currentValues,
      estimatedFclQuantity: sanitizedValue,
      status: 'Draft',
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      estimatedFclQuantity: '',
    }));
    setDraftMessage('');
  }

  function handleSearchChange(event) {
    const suggestionSupplierId = event.suggestion?.supplierId;

    setValues((currentValues) => ({
      ...currentValues,
      search: event.target.value,
    }));

    if (suggestionSupplierId) {
      addSupplier(suggestionSupplierId);
    }
  }

  function addSupplier(supplierId) {
    if (isSubmittedSummary) {
      return;
    }

    const supplier = supplierOptions.find(
      (option) => String(option.value) === String(supplierId)
    );

    if (!supplier || selectedSupplierIds.has(String(supplier.value))) {
      return;
    }

    pendingQuantityFocusSupplierId.current = String(supplier.value);

    setValues((currentValues) => {
      const nextItems = [
        ...currentValues.items,
        {
          supplierId: String(supplier.value),
          supplierName: supplier.label,
          sacksMark: supplier.sacksMark || '',
          quantity: '',
          warehouseLocation: currentValues.warehouseLocation || '',
        },
      ];

      return withItemsAndEstimatedFcl(currentValues, nextItems, {
        search: '',
        status: 'Draft',
      });
    });
    setErrors((currentErrors) => ({
      ...currentErrors,
      items: '',
    }));
    setDraftMessage('');
  }

  function removeSupplier(supplierId) {
    if (isSubmittedSummary) {
      return;
    }

    const itemToRemove = values.items.find(
      (item) => String(item.supplierId) === String(supplierId)
    );
    const purchaseOrder = findInitialSummaryPurchaseOrderForItem(
      purchaseOrders,
      values.poDate,
      itemToRemove
    );

    if (purchaseOrder && protectedPurchaseOrderIds.has(Number(purchaseOrder.id))) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        items:
          'This supplier already has a P.O. in Saved Balancing Records and cannot be removed from the submitted batch.',
      }));
      return;
    }

    if (isSelectedItemBatchLocked(purchaseOrders, values.poDate, itemToRemove)) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        items:
          'This supplier has a generated P.O. that is already in progress or received and cannot be removed from the submitted batch.',
      }));
      return;
    }

    if (purchaseOrder) {
      const removeWithGeneratedPurchaseOrder = async () => {
        const didRemovePurchaseOrder = await onRemoveGeneratedPurchaseOrder?.(purchaseOrder);

        if (!didRemovePurchaseOrder) {
          setErrors((currentErrors) => ({
            ...currentErrors,
            items:
              'Unable to remove the generated P.O. from the Purchase Order List. Please review the P.O. status first.',
          }));
          return;
        }

        removeSupplierFromValues(supplierId);
      };

      if (onRequestRemoveGeneratedPurchaseOrderConfirmation) {
        onRequestRemoveGeneratedPurchaseOrderConfirmation(purchaseOrder, removeWithGeneratedPurchaseOrder);
        return;
      }

      removeWithGeneratedPurchaseOrder();
      return;
    }

    removeSupplierFromValues(supplierId);
  }

  function removeSupplierFromValues(supplierId) {
    setValues((currentValues) => {
      const nextItems = currentValues.items.filter(
        (item) => String(item.supplierId) !== String(supplierId)
      );

      return withItemsAndEstimatedFcl(currentValues, nextItems, {
        status: 'Draft',
      });
    });
    setErrors((currentErrors) => ({
      ...currentErrors,
      items: '',
    }));
    setDraftMessage('');
  }

  async function handleReopenSubmittedSummary() {
    if (!isSubmittedSummary || isSubmitting || isDraftSaving) {
      return;
    }

    const reopen = async () => {
      setIsDraftSaving(true);
      setErrors({});
      setDraftMessage('');

      try {
        const savedInitialSummary = await saveInitialSummaryPurchaseOrder({
          ...values,
          status: 'Draft',
          items: withBatchWarehouseLocation(values.items, values.warehouseLocation),
        });

        skipNextAutoSave.current = true;
        setValues((currentValues) => ({
          ...currentValues,
          ...normalizeSavedInitialSummary(savedInitialSummary),
          search: '',
        }));
        onDraftSaved?.('Submitted Initial Summary P.O. reopened for editing.');
      } catch (error) {
        if (!isEmptyInitialSummaryMessage(error.message)) {
          setDraftMessage(error.message);
        }
      } finally {
        setIsDraftSaving(false);
      }
    };

    if (onRequestReopenConfirmation) {
      onRequestReopenConfirmation(reopen);
      return;
    }

    await reopen();
  }

  async function handleClearSuppliers() {
    if (isSubmittedSummary) {
      return;
    }

    const lockedItems = values.items.filter((item) =>
      isSelectedItemLocked(purchaseOrders, values.poDate, item, protectedPurchaseOrderIds)
    );
    const removablePurchaseOrders = values.items
      .filter(
        (item) =>
          !isSelectedItemLocked(purchaseOrders, values.poDate, item, protectedPurchaseOrderIds)
      )
      .map((item) => findInitialSummaryPurchaseOrderForItem(purchaseOrders, values.poDate, item))
      .filter(Boolean);

    for (const purchaseOrder of removablePurchaseOrders) {
      const didRemovePurchaseOrder = await onRemoveGeneratedPurchaseOrder?.(purchaseOrder);

      if (!didRemovePurchaseOrder) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          items:
            'Unable to remove one generated P.O. from the Purchase Order List. Please review the P.O. status first.',
        }));
        return;
      }
    }

    setValues((currentValues) => ({
      ...currentValues,
      search: '',
      status: 'Draft',
      items: lockedItems,
      estimatedFclQuantity: lockedItems.length ? currentValues.estimatedFclQuantity : '',
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      items: '',
    }));
    setDraftMessage('');
  }

  function handleQuantityChange(supplierId, quantity) {
    if (isSubmittedSummary) {
      return;
    }

    const selectedItem = values.items.find(
      (item) => String(item.supplierId) === String(supplierId)
    );

    if (isSelectedItemBatchLocked(purchaseOrders, values.poDate, selectedItem)) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [`quantity-${supplierId}`]: 'Locked',
      }));
      return;
    }

    const sanitizedQuantity = String(quantity || '').replace(/\D/g, '');

    setValues((currentValues) => {
      const nextItems = currentValues.items.map((item) =>
        String(item.supplierId) === String(supplierId)
          ? { ...item, quantity: sanitizedQuantity }
          : item
      );

      return withItemsAndEstimatedFcl(currentValues, nextItems, {
        status: 'Draft',
      });
    });
    setErrors((currentErrors) => ({
      ...currentErrors,
      [`quantity-${supplierId}`]: '',
    }));
    setDraftMessage('');
  }

  function handleBatchWarehouseLocationChange(event) {
    if (isSubmittedSummary) {
      return;
    }

    const normalizedWarehouseLocation = String(event.target.value || '').trim();

    setValues((currentValues) => ({
      ...currentValues,
      warehouseLocation: normalizedWarehouseLocation,
      status: 'Draft',
      items: currentValues.items.map((item) =>
        isSelectedItemBatchLocked(purchaseOrders, currentValues.poDate, item)
          ? item
          : { ...item, warehouseLocation: normalizedWarehouseLocation }
      ),
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      warehouseLocation: '',
    }));
    setDraftMessage('');
  }

  function handleDefaultWarehouseCheckboxChange(event) {
    if (isSubmittedSummary) {
      return;
    }

    if (event.target.checked) {
      if (!values.warehouseLocation) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          warehouseLocation: 'Select branch first',
        }));
        return;
      }

      setStoredDefaultWarehouseLocation(values.warehouseLocation);
      setDraftMessage('Default branch saved for future Supplier Batch P.O.');
      return;
    }

    clearStoredDefaultWarehouseLocation();
    setDraftMessage('Default branch cleared.');
  }

  async function handleSaveDraft() {
    if (isSubmittedSummary) {
      return;
    }

    const nextErrors = validateDraft(values);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsDraftSaving(true);

    try {
      await saveInitialSummaryPurchaseOrder({
        ...values,
        status: 'Draft',
        items: withBatchWarehouseLocation(values.items, values.warehouseLocation),
      });
      setDraftMessage('');
      onDraftSaved?.('Initial Summary P.O. draft saved.');
    } catch (error) {
      if (!isEmptyInitialSummaryMessage(error.message)) {
        setDraftMessage(error.message);
      }
    } finally {
      setIsDraftSaving(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmittedSummary || isSubmitting) {
      return;
    }

    const nextErrors = validateSubmit(values);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      window.requestAnimationFrame(() => {
        scrollToBatchError(nextErrors);
      });
      return;
    }

    if (onRequestSubmitConfirmation) {
      onRequestSubmitConfirmation(() => submitValues(values));
      return;
    }

    await submitValues(values);
  }

  async function submitValues(nextValues) {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      await saveInitialSummaryPurchaseOrder({
        ...nextValues,
        status: 'Draft',
        items: withBatchWarehouseLocation(nextValues.items, nextValues.warehouseLocation),
      });
    } catch (error) {
      if (!isEmptyInitialSummaryMessage(error.message)) {
        setDraftMessage(error.message);
      }
      setIsSubmitting(false);
      return;
    }

    const submitValuesWithWarehouse = {
      ...nextValues,
      items: withBatchWarehouseLocation(nextValues.items, nextValues.warehouseLocation),
    };
    const didSubmit = await onSubmit(submitValuesWithWarehouse);

    if (didSubmit) {
      try {
        await saveInitialSummaryPurchaseOrder({
          ...submitValuesWithWarehouse,
          status: 'Submitted',
        });
    } catch (error) {
      if (!isEmptyInitialSummaryMessage(error.message)) {
        setDraftMessage(error.message);
      }
        setIsSubmitting(false);
        return;
      }

      setValues(createInitialBatchValues());
      setErrors({});
      setDraftMessage('');
    }

    setIsSubmitting(false);
  }

  return (
    <form
      className="overflow-hidden rounded-[24px] border border-[var(--theme-border)] bg-white shadow-[0_14px_34px_rgba(var(--theme-shadow-rgb),0.07)]"
      onSubmit={handleSubmit}
    >
      {autoSaveBadgeToken ? (
        <span className="fixed right-4 top-[74px] z-30 inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)] shadow-[0_12px_30px_rgba(var(--theme-shadow-rgb),0.14)] sm:right-6 sm:top-[86px]">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          Auto Saved
        </span>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--theme-border-soft)] bg-white px-5 py-4 sm:px-6">
        <div>
          <h2 className="m-0 text-[1.22rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
            Supplier Batch P.O.
          </h2>
          <p className="m-0 mt-2 text-[0.92rem] text-[var(--theme-text-muted)]">
            Select active suppliers, enter sacks quantity, then save the batch as a draft or submit it.
          </p>
        </div>
      </div>

      <div className="grid gap-4 p-5 sm:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="grid w-full max-w-[760px] gap-3 sm:grid-cols-[minmax(220px,320px)_140px_140px]">
            <CustomDatePicker
              label="P.O. Date"
              name="poDate"
              value={values.poDate}
              error={errors.poDate}
              onChange={handleDateChange}
              highlightedDates={highlightedCalendarDates}
            />
            <div data-batch-error-target="priceMin">
              <PriceRangeInput
                label={getPriceRangeLabel('min', errors.priceMin)}
                name="priceMin"
                value={values.priceMin}
                error={errors.priceMin}
                onChange={handlePriceRangeChange}
                disabled={isSubmittedSummary}
              />
            </div>
            <div data-batch-error-target="priceMax">
              <PriceRangeInput
                label={getPriceRangeLabel('max', errors.priceMax)}
                name="priceMax"
                value={values.priceMax}
                error={errors.priceMax}
                onChange={handlePriceRangeChange}
                disabled={isSubmittedSummary}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isSubmittedSummary ? (
              <button
                className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] px-3 text-[0.78rem] font-semibold text-[var(--theme-accent-strong)] transition hover:bg-[var(--theme-accent-panel)] hover:text-[var(--theme-accent-panel-text)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
                type="button"
                onClick={handleReopenSubmittedSummary}
                disabled={isSaving || isDraftSaving || isSubmitting}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Reopen
              </button>
            ) : null}
            <InitialSummaryStatusBadge status={selectedDateStatus} />
          </div>
        </div>

        {!isPageLoading && !canSubmit ? (
          <FeedbackMessage
            tone="warning"
            message="Add or activate at least one supplier before submitting supplier batch P.O.s."
            action={
              onAddSupplier
                ? {
                    label: 'Add Supplier',
                    onClick: onAddSupplier,
                  }
                : null
            }
          />
        ) : null}
        {isDraftLoading ? (
          <FeedbackMessage tone="info" message="Loading Initial Summary P.O. draft..." />
        ) : null}
        {draftMessage ? <FeedbackMessage tone="info" message={draftMessage} /> : null}
        {errors.items ? (
          <div data-batch-error-target="items">
            <FeedbackMessage tone="error" message={errors.items} />
          </div>
        ) : null}

        <div className="relative grid min-h-[420px] gap-4 md:grid-cols-[minmax(0,0.92fr)_40px_minmax(0,1.08fr)]">
          <section className="flex min-h-0 flex-col rounded-[18px] border border-[var(--theme-border)] bg-white p-4">
            <div>
              <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                Active Suppliers
              </h3>
              <p className="m-0 mt-1 text-[0.78rem] text-[var(--theme-text-muted)]">
                Search or add suppliers to the batch.
              </p>
            </div>
            <div className="mt-4 w-full max-w-[300px]">
              <CustomSearchInput
                label="Search"
                name="supplierSearch"
                value={values.search}
                suggestions={supplierSuggestions}
                onChange={handleSearchChange}
                placeholder="Search supplier"
              />
            </div>
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              {filteredSuppliers.length ? (
                <div className="grid gap-2">
                  {filteredSuppliers.map((supplier) => (
                    <button
                      key={supplier.value}
                      className="flex min-h-[44px] items-center justify-between gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2 text-left transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:hover:border-[var(--theme-border-soft)]"
                      type="button"
                      onClick={() => addSupplier(supplier.value)}
                      disabled={isSubmittedSummary}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[0.86rem] font-semibold text-[var(--theme-text-strong)]">
                          {supplier.label}
                        </span>
                        {supplier.sacksMark ? (
                          <span className="block truncate text-[0.72rem] text-[var(--theme-text-muted)]">
                            {supplier.sacksMark}
                          </span>
                        ) : null}
                      </span>
                      <Plus className="h-4 w-4 shrink-0 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyPanel icon={<Search className="h-5 w-5" />} text="No suppliers found" />
              )}
            </div>
          </section>

          <div
            className="pointer-events-none hidden place-self-center rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] p-1.5 text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.20)] md:grid"
            aria-hidden="true"
          >
            <ArrowRight className="h-4 w-4 stroke-[2.5]" />
            <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
          </div>

          <div
            className="pointer-events-none inline-flex place-self-center rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-2 py-1.5 text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.20)] md:hidden"
            aria-hidden="true"
          >
            <ArrowUp className="h-4 w-4 stroke-[2.5]" />
            <ArrowDown className="h-4 w-4 stroke-[2.5]" />
          </div>

          <section className="flex min-h-0 flex-col rounded-[18px] border border-[var(--theme-border)] bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-text-strong)]">
                  Selected Suppliers
                </h3>
                <p className="m-0 mt-1 text-[0.78rem] text-[var(--theme-text-muted)]">
                  Enter sacks quantity for each supplier.
                </p>
              </div>
              <button
                className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-45"
                type="button"
                onClick={() =>
                  onRequestClearConfirmation
                    ? onRequestClearConfirmation(
                        handleClearSuppliers,
                        getClearSuppliersSummary(
                          purchaseOrders,
                          values.poDate,
                          values.items,
                          protectedPurchaseOrderIds
                        )
                      )
                    : handleClearSuppliers()
                }
                disabled={!values.items.length || isSubmittedSummary}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Clear
              </button>
            </div>
            <div className="mt-3 grid gap-2 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
              <div data-batch-error-target="warehouseLocation">
                <CustomSelect
                  label="Warehouse Location / Branch"
                  name="warehouseLocation"
                  value={values.warehouseLocation}
                  error={errors.warehouseLocation}
                  options={warehouseLocationOptions}
                  placeholder="Select branch for this batch"
                  clearOptionLabel="Select branch"
                  disabled={isSubmittedSummary}
                  onChange={handleBatchWarehouseLocationChange}
                  wrapperClassName="relative grid w-full min-w-0 gap-1.5"
                />
              </div>
              <label
                className={`inline-flex min-h-[40px] w-fit items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] sm:mt-[27px] ${
                  isSubmittedSummary
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer hover:bg-[var(--theme-surface-soft)]'
                }`}
                title="Remember this branch for future Supplier Batch P.O."
              >
                <input
                  className="h-4 w-4 accent-[var(--theme-accent)]"
                  type="checkbox"
                  checked={
                    Boolean(values.warehouseLocation) &&
                    values.warehouseLocation === getStoredDefaultWarehouseLocation()
                  }
                  onChange={handleDefaultWarehouseCheckboxChange}
                  disabled={isSubmittedSummary || !values.warehouseLocation}
                />
                Set as default
              </label>
            </div>

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              {values.items.length ? (
                <div className="grid gap-2">
                  {values.items.map((item) => {
                    const isProtected = isSelectedItemProtected(
                      purchaseOrders,
                      values.poDate,
                      item,
                      protectedPurchaseOrderIds
                    );
                    const lockedPurchaseOrder = getSelectedItemLockedPurchaseOrder(
                      purchaseOrders,
                      values.poDate,
                      item
                    );
                    const isBatchLocked = Boolean(lockedPurchaseOrder);
                    const isLocked = isProtected || isBatchLocked;
                    const lockMessage = isProtected
                      ? 'Locked by Saved Balancing Records.'
                      : isBatchLocked
                        ? `Locked because the generated P.O. is already ${lockedPurchaseOrder.status}.`
                        : '';

                    return (
                    <div
                      key={item.supplierId}
                      className="grid items-end gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-3 md:grid-cols-[minmax(0,1fr)_170px_44px]"
                    >
                      <div className="min-w-0">
                        <strong className="block truncate text-[0.9rem] text-[var(--theme-text-strong)]">
                          {item.supplierName}
                        </strong>
                        {item.sacksMark ? (
                          <span className="mt-0.5 block truncate text-[0.74rem] text-[var(--theme-text-muted)]">
                            {item.sacksMark}
                          </span>
                        ) : null}
                      </div>
                      <label className="grid gap-1" data-batch-error-target={`quantity-${item.supplierId}`}>
                        <span
                          className={`text-[0.7rem] font-medium ${
                            errors[`quantity-${item.supplierId}`]
                              ? 'text-[#b94f43]'
                              : 'text-[var(--theme-text-subtle)]'
                          }`}
                        >
                          {errors[`quantity-${item.supplierId}`]
                            ? 'No. of Sacks required'
                            : 'No. of Sacks'}
                        </span>
                        <input
                          ref={(element) => {
                            const supplierId = String(item.supplierId);

                            if (element) {
                              quantityInputRefs.current.set(supplierId, element);
                              return;
                            }

                            quantityInputRefs.current.delete(supplierId);
                          }}
                          className={`h-[38px] rounded-full border bg-white px-3 text-[0.84rem] outline-none disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] ${
                            errors[`quantity-${item.supplierId}`]
                              ? 'border-[#d78b7d]'
                              : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]'
                          }`}
                          type="text"
                          inputMode="numeric"
                          name={`quantity-${item.supplierId}`}
                          value={item.quantity}
                          onChange={(event) =>
                            handleQuantityChange(item.supplierId, event.target.value)
                          }
                          disabled={isSubmittedSummary || isLocked}
                          placeholder="0"
                          title={lockMessage || undefined}
                        />
                      </label>
                      <button
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#efd7d3] bg-white text-[#b95a4d] transition hover:bg-[#fff1ef] focus:outline-none focus:ring-2 focus:ring-[#e5aaa1] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
                        type="button"
                        onClick={() => removeSupplier(item.supplierId)}
                        disabled={isSubmittedSummary || isLocked}
                        aria-label={
                          isLocked
                            ? `${item.supplierName} cannot be removed because its generated P.O. is locked`
                            : `Remove ${item.supplierName}`
                        }
                        title={
                          isLocked
                            ? lockMessage
                            : `Remove ${item.supplierName}`
                        }
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {isLocked ? (
                        <p className="m-0 text-[0.72rem] font-medium text-[var(--theme-text-muted)] md:col-span-3">
                          {lockMessage}
                        </p>
                      ) : null}
                    </div>
                    );
                  })}
                  <SelectedSacksSummary
                    totalSacks={totalSacks}
                    estimatedFclQuantity={values.estimatedFclQuantity}
                    error={errors.estimatedFclQuantity}
                    onFclQuantityChange={handleFclQuantityChange}
                    disabled={isSubmittedSummary}
                  />
                </div>
              ) : (
                <EmptyPanel text="No selected suppliers" />
              )}
            </div>
          </section>
        </div>

        <div className="flex flex-wrap justify-end gap-3">
          <button
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[14px] border border-[var(--theme-border)] bg-white px-5 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
            type="button"
            onClick={handleSaveDraft}
            disabled={isSaving || isDraftSaving || isSubmitting || isSubmittedSummary}
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            {isDraftSaving ? 'Saving Draft...' : 'Save as Draft'}
          </button>
          <button
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[14px] bg-[var(--theme-accent)] px-5 text-[0.9rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="submit"
            disabled={isSaving || isSubmitting || !canSubmit || isSubmittedSummary}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {isSaving || isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </form>
  );
}

function EmptyPanel({ icon = null, text }) {
  return (
    <div className="grid min-h-[120px] place-items-center rounded-[14px] border border-dashed border-[var(--theme-border)] bg-white px-4 text-center text-[0.84rem] text-[var(--theme-text-muted)]">
      <span className="inline-flex items-center gap-2">
        {icon}
        {text}
      </span>
    </div>
  );
}

function PriceRangeInput({
  label,
  name,
  value,
  error,
  onChange,
  disabled = false,
}) {
  return (
    <label className="grid min-w-0 gap-1.5">
      <span
        className={`text-[0.74rem] font-medium ${
          error ? 'text-[#b94f43]' : 'text-[var(--theme-text-subtle)]'
        }`}
      >
        {label}
      </span>
      <input
        className={`min-h-[40px] w-full min-w-0 rounded-full border bg-white px-3 text-[0.84rem] outline-none placeholder:text-[var(--theme-text-muted)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] ${
          error ? 'border-[#d78b7d]' : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]'
        }`}
        type="text"
        inputMode="decimal"
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder="0.00"
      />
    </label>
  );
}

function SelectedSacksSummary({
  totalSacks,
  estimatedFclQuantity,
  error,
  onFclQuantityChange,
  disabled,
}) {
  return (
    <div className="mt-2 grid gap-3 rounded-[14px] border border-[var(--theme-accent)] bg-[var(--theme-accent-panel)] p-3 sm:grid-cols-[minmax(0,1fr)_170px]">
      <div className="grid gap-1">
        <span className="text-[0.72rem] font-medium text-[var(--theme-accent-panel-text)]">
          Total No. of Sacks
        </span>
        <strong className="text-[1.1rem] font-semibold text-[var(--theme-accent-panel-text)]">
          {formatInteger(totalSacks)}
        </strong>
        <span className="text-[0.72rem] text-[var(--theme-accent-panel-text)]">
          Static estimate: {SACKS_PER_FCL} sacks per FCL
        </span>
      </div>
      <label className="grid gap-1">
        <span
          className={`text-[0.72rem] font-medium ${
            error ? 'text-[#b94f43]' : 'text-[var(--theme-accent-panel-text)]'
          }`}
        >
          {error ? 'Estimated FCL required' : 'Estimated FCL Quantity'}
        </span>
        <input
          className={`h-[38px] w-[calc(100%-12px)] rounded-full border bg-white px-3 text-[0.84rem] outline-none disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] ${
            error ? 'border-[#d78b7d]' : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]'
          }`}
          type="text"
          inputMode="numeric"
          name="estimatedFclQuantity"
          value={estimatedFclQuantity}
          onChange={onFclQuantityChange}
          disabled={disabled}
          placeholder="0"
        />
      </label>
    </div>
  );
}

function InitialSummaryStatusBadge({ status }) {
  const statusConfig = {
    draft: {
      label: 'Draft',
      className: 'border-[#f0d49a] bg-[#fff8e6] text-[#9a6a12]',
    },
    submitted: {
      label: 'Submitted',
      className: 'border-[var(--theme-border)] bg-[var(--theme-accent-soft)] text-[var(--theme-text-subtle)]',
    },
    'no-entry': {
      label: 'No Entry',
      className: 'border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-text-muted)]',
    },
  };
  const config = statusConfig[status] || statusConfig['no-entry'];

  return (
    <div className="flex min-h-[42px] items-center justify-end">
      <span
        className={`inline-flex min-h-[34px] items-center rounded-full border px-3.5 text-[0.76rem] font-semibold uppercase tracking-[0.08em] ${config.className}`}
      >
        {config.label}
      </span>
    </div>
  );
}

function createInitialBatchValues() {
  const baseValues = createPurchaseOrderFormInitialValues();

  return {
    poDate: baseValues.poDate,
    warehouseLocation: getStoredDefaultWarehouseLocation(),
    priceMin: '',
    priceMax: '',
    estimatedFclQuantity: '',
    status: 'Draft',
    search: '',
    items: [],
  };
}

function getStoredDefaultWarehouseLocation() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return '';
  }

  try {
    return String(window.localStorage.getItem(SUPPLIER_BATCH_DEFAULT_WAREHOUSE_KEY) || '').trim();
  } catch {
    return '';
  }
}

function setStoredDefaultWarehouseLocation(warehouseLocation) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(
      SUPPLIER_BATCH_DEFAULT_WAREHOUSE_KEY,
      String(warehouseLocation || '').trim()
    );
  } catch {
    // Ignore storage failures; the selected branch still applies to the current batch.
  }
}

function clearStoredDefaultWarehouseLocation() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.removeItem(SUPPLIER_BATCH_DEFAULT_WAREHOUSE_KEY);
  } catch {
    // Ignore storage failures; clearing the default is best-effort.
  }
}

function normalizeSavedInitialSummary(savedInitialSummary) {
  const savedItems = Array.isArray(savedInitialSummary.items)
    ? savedInitialSummary.items
        .map((item) => ({
          supplierId: String(item.supplierId || ''),
          supplierName: String(item.supplierName || ''),
          sacksMark: String(item.sacksMark || ''),
          quantity: String(item.quantity || ''),
          warehouseLocation: String(item.warehouseLocation || ''),
          purchaseOrderId:
            item.purchaseOrderId === null || typeof item.purchaseOrderId === 'undefined'
              ? ''
              : String(item.purchaseOrderId),
        }))
        .filter((item) => item.supplierId && item.supplierName)
    : [];

  return {
    poDate: savedInitialSummary.poDate || createInitialBatchValues().poDate,
    warehouseLocation: getSavedInitialSummaryWarehouseLocation(savedItems),
    priceMin: formatSavedMoney(savedInitialSummary.priceMin),
    priceMax: formatSavedMoney(savedInitialSummary.priceMax),
    estimatedFclQuantity:
      savedInitialSummary.estimatedFclQuantity === null ||
      typeof savedInitialSummary.estimatedFclQuantity === 'undefined'
        ? getEstimatedFclQuantity(savedItems)
        : String(Number(savedInitialSummary.estimatedFclQuantity)),
    status: savedInitialSummary.status || 'Draft',
    search: '',
    items: savedItems,
  };
}

function getSavedInitialSummaryWarehouseLocation(items = []) {
  const warehouseLocations = Array.from(
    new Set(
      items
        .map((item) => String(item.warehouseLocation || '').trim())
        .filter(Boolean)
    )
  );

  return warehouseLocations.length === 1 ? warehouseLocations[0] : '';
}

function withBatchWarehouseLocation(items = [], warehouseLocation = '') {
  const normalizedWarehouseLocation = String(warehouseLocation || '').trim();

  return items.map((item) => ({
    ...item,
    warehouseLocation: normalizedWarehouseLocation,
  }));
}

function validateDraft(values) {
  const errors = {};

  if (!values.poDate) {
    errors.poDate = 'P.O. date is required.';
  }

  if (!values.items.length) {
    errors.items = 'Add at least one supplier before saving a draft.';
  }

  Object.assign(errors, validatePriceRange(values));

  return errors;
}

function hasPriceRangeValues(values) {
  return (
    String(values.priceMin || '').trim() !== '' ||
    String(values.priceMax || '').trim() !== ''
  );
}

function getSupplierSearchText(supplier) {
  return [
    supplier?.label,
    supplier?.sacksMark,
  ]
    .filter(Boolean)
    .join(' ');
}

function validateSubmit(values) {
  const errors = validateDraft(values);
  const hasMinPrice = String(values.priceMin || '').trim() !== '';
  const hasMaxPrice = String(values.priceMax || '').trim() !== '';
  const totalSacks = getTotalSacks(values.items);
  const estimatedFclQuantity = Number(values.estimatedFclQuantity);

  if (!hasMinPrice) {
    errors.priceMin = 'Required';
  }

  if (!hasMaxPrice) {
    errors.priceMax = 'Required';
  }

  if (!values.items.length) {
    errors.items = 'Add at least one supplier before submitting Supplier Batch P.O.';
  }

  if (!String(values.warehouseLocation || '').trim()) {
    errors.warehouseLocation = 'Warehouse is required';
  }

  values.items.forEach((item) => {
    const quantity = Number(item.quantity);

    if (!Number.isInteger(quantity) || quantity <= 0) {
      errors[`quantity-${item.supplierId}`] = 'Required';
    }
  });

  if (
    totalSacks > 0 &&
    (!Number.isFinite(estimatedFclQuantity) || estimatedFclQuantity <= 0)
  ) {
    errors.estimatedFclQuantity = 'Required';
  }

  return errors;
}

function scrollToBatchError(errors = {}) {
  const targetNames = [
    'priceMin',
    'priceMax',
    'items',
    'warehouseLocation',
    'estimatedFclQuantity',
    ...Object.keys(errors).filter((key) => key.startsWith('quantity-')),
  ];
  const targetName = targetNames.find((name) => errors[name]);
  const target = targetName
    ? document.querySelector(`[data-batch-error-target="${targetName}"]`)
    : null;

  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}

function validatePriceRange(values) {
  const errors = {};
  const hasMinPrice = String(values.priceMin || '').trim() !== '';
  const hasMaxPrice = String(values.priceMax || '').trim() !== '';

  if (hasMinPrice !== hasMaxPrice) {
    if (!hasMinPrice) {
      errors.priceMin = 'Required';
    }

    if (!hasMaxPrice) {
      errors.priceMax = 'Required';
    }

    return errors;
  }

  if (!hasMinPrice && !hasMaxPrice) {
    return errors;
  }

  const priceMin = Number(values.priceMin);
  const priceMax = Number(values.priceMax);

  if (!Number.isFinite(priceMin) || priceMin < 0) {
    errors.priceMin = 'Invalid';
  }

  if (!Number.isFinite(priceMax) || priceMax < 0) {
    errors.priceMax = 'Invalid';
  }

  if (!errors.priceMin && !errors.priceMax && priceMax < priceMin) {
    errors.priceMax = 'Max must be higher';
  }

  return errors;
}

function buildProtectedPurchaseOrderIdSet(supplierBalancings = []) {
  return new Set(
    supplierBalancings.flatMap((record) =>
      (record.purchaseOrderIds || [])
        .map((purchaseOrderId) => Number(purchaseOrderId))
        .filter((purchaseOrderId) => Number.isInteger(purchaseOrderId) && purchaseOrderId > 0)
    )
  );
}

function isSelectedItemProtected(purchaseOrders, poDate, item, protectedPurchaseOrderIds) {
  const purchaseOrder = findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, item);

  return Boolean(purchaseOrder && protectedPurchaseOrderIds.has(Number(purchaseOrder.id)));
}

function isSelectedItemLocked(purchaseOrders, poDate, item, protectedPurchaseOrderIds) {
  return (
    isSelectedItemProtected(purchaseOrders, poDate, item, protectedPurchaseOrderIds) ||
    isSelectedItemBatchLocked(purchaseOrders, poDate, item)
  );
}

function getClearSuppliersSummary(purchaseOrders, poDate, items = [], protectedPurchaseOrderIds) {
  const lockedItems = items.filter((item) =>
    isSelectedItemLocked(purchaseOrders, poDate, item, protectedPurchaseOrderIds)
  );
  const editableCount = Math.max(0, items.length - lockedItems.length);
  const lockedStatuses = lockedItems
    .map((item) => {
      const purchaseOrder = findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, item);

      if (!purchaseOrder) {
        return '';
      }

      if (protectedPurchaseOrderIds.has(Number(purchaseOrder.id))) {
        return 'Saved Balancing';
      }

      return purchaseOrder.status || '';
    })
    .filter(Boolean);
  const uniqueLockedStatuses = Array.from(new Set(lockedStatuses));

  return {
    editableCount,
    lockedCount: lockedItems.length,
    lockedStatuses: uniqueLockedStatuses,
    totalCount: items.length,
  };
}

function isSelectedItemBatchLocked(purchaseOrders, poDate, item) {
  return Boolean(getSelectedItemLockedPurchaseOrder(purchaseOrders, poDate, item));
}

function getSelectedItemLockedPurchaseOrder(purchaseOrders, poDate, item) {
  const purchaseOrder = findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, item);

  if (!purchaseOrder) {
    return null;
  }

  return purchaseOrder.status === 'Initial' ? null : purchaseOrder;
}

function findInitialSummaryPurchaseOrderForItem(purchaseOrders = [], poDate, item = {}) {
  const purchaseOrderId = Number(item.purchaseOrderId);

  if (Number.isInteger(purchaseOrderId) && purchaseOrderId > 0) {
    const matchedById = purchaseOrders.find(
      (purchaseOrder) => Number(purchaseOrder.id) === purchaseOrderId
    );

    if (matchedById) {
      return matchedById;
    }
  }

  const normalizedPoDate = toDateInputValue(poDate);
  const normalizedSupplierId = String(item.supplierId || '');

  return purchaseOrders.find(
    (purchaseOrder) =>
      String(purchaseOrder.status || '') !== 'Cancelled' &&
      toDateInputValue(purchaseOrder.poDate) === normalizedPoDate &&
      String(purchaseOrder.supplierId) === normalizedSupplierId &&
      purchaseOrder.stockUnit === 'sacks'
  );
}

function sanitizeMoneyInput(value) {
  const normalizedValue = String(value || '').replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = normalizedValue.split('.');
  const decimalPart = decimalParts.join('').slice(0, 2);

  return decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
}

function formatSavedMoney(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '';
  }

  return Number(value).toFixed(2);
}

function withItemsAndEstimatedFcl(currentValues, nextItems, extraValues = {}) {
  const currentAutoEstimate = getEstimatedFclQuantity(currentValues.items);
  const currentFclQuantity = String(currentValues.estimatedFclQuantity || '').trim();
  const shouldUseAutoEstimate =
    !currentFclQuantity || currentFclQuantity === currentAutoEstimate;

  return {
    ...currentValues,
    ...extraValues,
    items: nextItems,
    estimatedFclQuantity: shouldUseAutoEstimate
      ? getEstimatedFclQuantity(nextItems)
      : currentValues.estimatedFclQuantity,
  };
}

function getTotalSacks(items = []) {
  return items.reduce((total, item) => total + Number(item.quantity || 0), 0);
}

function getEstimatedFclQuantity(items = []) {
  const totalSacks = getTotalSacks(items);

  return totalSacks > 0 ? String(Math.ceil(totalSacks / SACKS_PER_FCL)) : '';
}

function formatInteger(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function parseDateInputValue(value) {
  const [year, month, day] = String(value || '').split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
}

function getPriceRangeLabel(type, error) {
  if (!error) {
    return type === 'min' ? 'Min Price' : 'Max Price';
  }

  if (error === 'Required') {
    return type === 'min' ? 'Min required' : 'Max required';
  }

  if (error === 'Max must be higher') {
    return 'Max must be higher';
  }

  return type === 'min' ? 'Invalid min' : 'Invalid max';
}

function isEmptyInitialSummaryMessage(message) {
  return String(message || '').includes(
    'Add at least one supplier before saving an Initial Summary P.O.'
  );
}

export default PurchaseOrderSupplierBatch;
